<template>
    <main class="dynamic-page">
        <component :is="pageComponent" v-if="pageComponent !== null" :data="pageData" />

        <StructureSection
            v-for="(section, index) in page?.structure"
            :key="index"
            :count="index"
            :content="section"
        />
    </main>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, provide, onBeforeUnmount, onMounted } from 'vue'
import { showError, useRoute, useAsyncData, useLocalisation, usePage } from '#imports'
import { hblDetailData } from '@/utils/helper'

useRouteCache((helper) => {
    helper.setMaxAge(60).setCacheable()
})

/*
 * Page request and error handling
 */
const route = useRoute()
const { page, getPage } = usePage()
const { platformBaseUrl } = useRuntimeConfig().public

const { data, error } = await useAsyncData(`page-${route.path}`, () => getPage(route), { initialCache: false })
page.value = data.value

if (error.value) {
    showError(error.value as Error)
}

// @ts-expect-error redirectCode is not in type definition
if (data?.value?.redirectCode != null) {
    // @ts-expect-error redirectTarget is not in type definition
    await navigateTo(data.value.redirectTarget, { redirectCode: data.value.redirectCode, external: data.value.redirectTarget.includes(platformBaseUrl) })
}

const PageCategory = defineAsyncComponent(() => import('@/components/page/PageCategory.vue'))
const PageDetail = defineAsyncComponent(() => import('@/components/page/PageDetail.vue'))
const PageCms = defineAsyncComponent(() => import('@/components/page/PageCms.vue'))

/*
 * Page components and data used for SEO / Head
 */
const pageComponent = computed(() => {
    if (page.value?.type === 'category') {
        return PageCategory
    }

    if (page.value?.type === 'detail') {
        return PageDetail
    }

    if (page.value?.type === 'cms') {
        return PageCms
    }

    // if does not exist, keeping for backwards compatibility
    if (page.value?.type === 'post') {
        return null
    }

    return null
})

const pageData = computed(() => {
    if (page.value?.type === 'category') {
        return page.value.category
    }

    if (page.value?.type === 'detail') {
        return page.value.detail
    }

    if (page.value?.type === 'cms') {
        return page.value.cms
    }

    if (page.value?.type === 'post') {
        return page.value.cms
    }

    return null
})

/*
 * Provide detail data to inject in detail structure (has no data)
 */
if (page.value?.type === 'detail' && page.value?.detail !== undefined) {
    provide(hblDetailData, page.value.detail)
    provide('hblBreadcrumbData', page.value?.breadcrumb)
}

if (page.value?.type === 'category' && page.value?.category !== undefined) {
    provide('hblBreadcrumbData', page.value?.breadcrumb)
}

/*
 * i18n
 */
const { entityPathInfo } = useLocalisation()

onMounted(() => {
    if (page.value?.type === 'category' && page.value?.category !== undefined) {
        entityPathInfo.value = page.value.category.pathInfo
    }

    if (page.value?.type === 'detail' && page.value?.detail !== undefined) {
        entityPathInfo.value = page.value.detail.pathInfo
    }
})

onBeforeUnmount(() => {
    entityPathInfo.value = null
})
</script>
